import { useEffect, useState, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { Navigate, useParams, useSearchParams } from 'react-router-dom';
/*
 * `Link` e `useNavigate` vêm de `core/rotas`, NÃO de `react-router-dom`.
 *
 * O painel inteiro mora sob `/:slug` (`/oticavergueiro/home`), e os destes
 * aqui injetam o slug do cliente ativo; os do react-router mandariam para
 * `/home` cru, que não casa com rota nenhuma. Era exatamente esse o defeito:
 * "Ir para o meu painel", o fim da redefinição de senha, a saída de
 * `/criar-organizacao` e o "Ver plano" da conta suspensa levavam todos a uma
 * tela de não encontrado. As rotas de entrada (`/login`, `/criar-conta`...)
 * continuam sem prefixo pela lista `SEM_PREFIXO` do próprio módulo.
 */
import { Link, useNavigate, useIrParaAConta, comSlug } from '../../core/rotas';
import { useAuth } from '../../core/auth';
import { clienteDoHost, ehLocalhost } from '../../core/dominios';
import { supabase } from '../../core/supabase';
import { Banner, Button, Field } from '../../ui';
import { Icone } from '../../ui/icones';
import { NotFound } from './States';
import { FORMAS, logoDaMarca, tokensDaCor } from '../../ui/marca';
import { useTheme } from '../../ui/theme';
import { Marca } from '../../ui/lockup';
import orbita from '../../ds/assets/hub/grafismos/yardhub-orbita.svg';
import './acesso.css';

/**
 * A casca de TODA rota de entrada (`ROTAS-AUTH.md`, seção 0).
 *
 * Palco de marca à esquerda e o formulário à direita; no celular o palco
 * vira uma faixa curta no topo. O palco é quem responde "onde estou tentando
 * entrar" antes do primeiro campo.
 *
 * F3 do redesenho (26/10/2026): na porta de um cliente o palco é DELE. O logo
 * (ou o monograma, quando não há logo) ocupa o canto onde ficava a yard
 * suite, a cena usa a cor de destaque dele no lugar dos cartões amarelos da
 * Yard, e o botão principal sai em `--accent`. A Yard fica no rodapé, como
 * assinatura (regra 5 do contrato de marca: o logo do cliente vem antes e
 * separado). A foto de entrada (`login_image_url`, enviada em Configurações,
 * Marca) segue o `login_layout` escolhido lá: `lado` enche o palco, `capa`
 * vai atrás da tela inteira. Antes a foto aparecia sob um véu branco de 90%,
 * quase invisível, e o layout era ignorado.
 */
function Card({
  title,
  subtitle,
  marca,
  equipe = false,
  children,
}: {
  title?: string;
  subtitle?: string;
  marca?: MarcaPublica | null;
  /** Porta da equipe Yard: sem identidade de cliente nenhuma (seção 1). */
  equipe?: boolean;
  children: ReactNode;
}) {
  const cliente = !equipe && marca ? marca : null;
  const foto = cliente?.login_image_url || '';
  const layout = foto ? (cliente?.login_layout === 'capa' ? 'capa' : 'lado') : 'centro';
  const { theme } = useTheme();

  return (
    <div
      className={`app-auth suite-modern ${equipe ? 'auth-hub--equipe' : cliente ? 'auth-hub--cliente' : 'auth-hub--suite'}${foto ? ` auth-hub--foto auth-hub--${layout}` : ''}`}
      style={{
        ...(marca ? estiloDaMarca(marca, theme) : {}),
        ...(layout === 'capa' ? { backgroundImage: `url(${JSON.stringify(foto)})` } : {}),
      } as CSSProperties}
    >
      <aside
        className="auth-marca"
        style={layout === 'lado' ? { backgroundImage: `url(${JSON.stringify(foto)})` } : undefined}
      >
        <div className="auth-lk">
          {cliente ? (
            <MarcaDoCliente marca={cliente} />
          ) : (
            <Marca produto={equipe ? 'console' : 'suite'} registrada={!equipe} tamanho="inherit" />
          )}
        </div>

        <div className="auth-org">
          <p className="contexto">
            {equipe ? 'Console da equipe Yard' : cliente ? 'Área da equipe' : 'Plataforma para times de marketing'}
          </p>
          {/* `title`: no celular o nome longo é cortado em duas linhas. */}
          <div className="nome" title={cliente?.nome}>
            {equipe ? (
              <>Cada cliente, <mark>um olhar</mark>.</>
            ) : cliente ? (
              cliente.nome
            ) : (
              <>Marketing, CRM, conversas e vitrine. <mark>Na mesma conta.</mark></>
            )}
          </div>
          {(equipe || !cliente || cliente.login_frase) && (
            <p className="frase">
              {equipe
                ? 'Porta da agência. Daqui se entra no console e, de lá, na conta de um cliente.'
                : (cliente?.login_frase ??
                  'Campanhas, contatos, atendimento e a vitrine da empresa, no mesmo lugar e com a mesma linguagem.')}
            </p>
          )}
          {cliente
            ? !foto && <CenaDoCliente marca={cliente} />
            : (
              <div className="auth-cena" aria-hidden="true">
                <div className="auth-cena__cartao auth-cena__cartao--branco">
                  <small>Uma mesma linguagem</small>
                  <strong>Uma ideia.<br />Muitas possibilidades.</strong>
                  <div className="auth-cena__familias"><i /><i /><i /><i /></div>
                </div>
                <div className="auth-cena__cartao auth-cena__cartao--amarelo">
                  <small>{equipe ? 'Equipe Yard' : 'Feito em equipe'}</small>
                  <strong>{equipe ? 'Todos os clientes, uma mesa.' : 'Da primeira ideia à próxima conquista.'}</strong>
                  <span className="auth-cena__selo"><Icone.CheckCirculo tamanho={13} /> Tudo se conecta</span>
                </div>
                <img className="auth-cena__orbita" src={orbita} alt="" />
              </div>
            )}
        </div>

        <div className="auth-rodape-marca">
          {cliente ? (
            <>Uma solução <Marca produto="suite" tamanho="inherit" /></>
          ) : (
            <Marca produto="suite" tamanho="inherit" />
          )}
        </div>
      </aside>

      <div className="auth-lado">
        {/* `key` pelo título: cada etapa (entrar, link enviado, conta
            criada) entra com o próprio movimento curto, em vez de o cartão
            trocar o miolo de uma vez. Erros dentro da mesma etapa não mudam
            o título e não remontam o formulário. */}
        <div className="auth-card stack" key={title ?? 'porta'}>
          <div className="auth-cabeca">
            {cliente && (
              <span className="auth-eyebrow"><i aria-hidden="true" />{cliente.nome}</span>
            )}
            {equipe && <span className="auth-eyebrow"><i aria-hidden="true" />Equipe Yard</span>}
            {/* O `h1` é sempre semântico. Quando o palco já diz o nome em
                desenho, ele fica só para leitor de tela. */}
            {title ? (
              <h1 className="auth-titulo">{title}</h1>
            ) : (
              <h1 className="sr">{cliente?.nome ? `Entrar em ${cliente.nome}` : 'Entrar'}</h1>
            )}
            {subtitle && <p className="auth-sub">{subtitle}</p>}
          </div>
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * Duas letras para o monograma: as iniciais das duas primeiras palavras que
 * contam ("Construtora Horizonte Ltda" vira "CH"). O que está entre
 * parênteses e as ligas (de, da, Ltda) não contam; uma palavra só dá as duas
 * primeiras letras dela ("CIMA (teste local)" vira "CI").
 */
export function iniciais(nome: string): string {
  const palavras = nome
    .replace(/\(.*?\)/g, ' ')
    .split(/\s+/)
    .filter((p) => p && !/^(de|da|do|das|dos|e|ltda\.?|s\.?a\.?|me|eireli)$/i.test(p));
  const [a = '', b = ''] = palavras;
  if (!a) return '?';
  return (b ? a.charAt(0) + b.charAt(0) : a.slice(0, 2)).toUpperCase();
}

/**
 * O canto da marca na porta do cliente. Com logo, o logo certo para o fundo
 * claro do palco (`logoDaMarca`, V35); sem logo, o monograma na cor de
 * destaque com o nome ao lado, para a porta nunca abrir sem identidade.
 * Logo que falha ao carregar cai no monograma também.
 */
function MarcaDoCliente({ marca }: { marca: MarcaPublica }) {
  const logo = logoDaMarca(marca, 'light');
  const [quebrou, setQuebrou] = useState(false);
  if (logo && !quebrou) {
    return <img className="auth-logo" src={logo} alt={marca.nome} onError={() => setQuebrou(true)} />;
  }
  return (
    <span className="auth-monograma-lk">
      <span className="auth-monograma" aria-hidden="true">{iniciais(marca.nome)}</span>
      <span className="auth-monograma-lk__nome">{marca.nome}</span>
    </span>
  );
}

/**
 * A cena do palco na porta do cliente: aros concêntricos na cor de destaque
 * dele em volta do ícone (ou do monograma). Forma, não dado: nenhum número
 * nem frase inventada. Os aros respiram devagar; parado com movimento
 * reduzido.
 */
function CenaDoCliente({ marca }: { marca: MarcaPublica }) {
  // O ícone quadrado cabe no centro; sem ele, o logo inteiro num selo mais
  // largo; sem nenhum dos dois, o monograma.
  const icone = marca.icon_url || '';
  const logo = icone ? '' : logoDaMarca(marca, 'light');
  return (
    <div className="auth-cena auth-cena--cliente" aria-hidden="true">
      <span className="auth-cena__aro" />
      <span className="auth-cena__aro" />
      <span className="auth-cena__aro" />
      <span className={`auth-cena__centro${logo ? ' auth-cena__centro--logo' : ''}`}>
        {icone || logo ? <img src={icone || logo} alt="" /> : <span>{iniciais(marca.nome)}</span>}
      </span>
    </div>
  );
}

/**
 * Campo de senha com o botão de mostrar/ocultar (`ROTAS-AUTH.md`, seção 5).
 *
 * O botão é `aria-pressed` com rótulo que muda de "Mostrar senha" para
 * "Ocultar senha": só trocar o ícone deixaria quem lê por áudio sem saber em
 * que estado o campo está.
 */
function CampoDeSenha({
  label,
  value,
  onChange,
  autoComplete,
  help,
  error,
  onBlur,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  autoComplete: 'current-password' | 'new-password';
  help?: string;
  error?: string;
  onBlur?: () => void;
  required?: boolean;
}) {
  const [visivel, setVisivel] = useState(false);
  return (
    <div className="campo-senha">
      <Field
        label={label}
        type={visivel ? 'text' : 'password'}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        autoComplete={autoComplete}
        required={required}
        help={help}
        error={error}
      />
      <button
        type="button"
        className="olho"
        aria-pressed={visivel}
        aria-label={visivel ? 'Ocultar senha' : 'Mostrar senha'}
        onClick={() => setVisivel((v) => !v)}
      >
        {visivel ? <Icone.OlhoFechado tamanho={17} /> : <Icone.Olho tamanho={17} />}
      </button>
    </div>
  );
}

/**
 * As regras de senha, conferidas A CADA TECLA (seção 4: "nunca só depois do
 * envio"). Descobrir que a senha era curta só ao apertar "Criar conta" é o
 * tipo de ida e volta que o checklist existe para evitar.
 */
const REGRAS_DE_SENHA = [
  { id: 'tamanho', texto: 'Ao menos 10 caracteres', ok: (s: string) => s.length >= 10 },
  { id: 'letra', texto: 'Uma letra', ok: (s: string) => /\p{L}/u.test(s) },
  { id: 'numero', texto: 'Um número', ok: (s: string) => /\d/.test(s) },
];

export const senhaValida = (s: string) => REGRAS_DE_SENHA.every((r) => r.ok(s));

function ChecklistDeSenha({ senha }: { senha: string }) {
  return (
    <ul className="req-lista">
      {REGRAS_DE_SENHA.map((r) => {
        const ok = r.ok(senha);
        return (
          <li key={r.id} className={`req-item${ok ? ' ok' : ''}`}>
            {ok ? <Icone.CheckCirculo tamanho={16} /> : <Icone.ErroCirculo tamanho={16} />}
            {r.texto}
          </li>
        );
      })}
    </ul>
  );
}

/** O que a tela de entrada mostra antes de existir sessão. */
export type MarcaPublica = {
  nome: string;
  slug: string;
  /** C2: a marca volta mesmo suspensa - `marca_publica` não filtra mais por
   *  status. É esta tela que decide o que mostrar. */
  status: string;
  logo_url: string | null;
  logo_dark: string | null;
  icon_url: string | null;
  site_url: string | null;
  accent: string | null;
  accent_alt: string | null;
  brand_theme: string | null;
  brand_radius: string | null;
  /** Desenho da tela de entrada, configurado em Marca (migração 0028). */
  login_image_url: string | null;
  login_layout: string | null;
  login_frase: string | null;
};

const estiloDaMarca = (m: MarcaPublica, tema: 'light' | 'dark') =>
  ({
    ...(m.accent ? tokensDaCor(m.accent, tema) : {}),
    ...(m.accent_alt ? { '--accent-alt': m.accent_alt } : {}),
    ...(FORMAS[m.brand_radius ?? 'padrao'] ?? {}),
  }) as CSSProperties;

/**
 * Carrega a marca do cliente pelo subdomínio (`cima.yardsuite.com.br`) ou pelo
 * caminho (`/entrar/cima`), que existe para quem ainda não apontou o DNS e para
 * o link que a Yard manda antes de o domínio propagar.
 *
 * Enquanto não responde, devolve `undefined`: pintar a tela de amarelo e trocar
 * para a marca do cliente meio segundo depois é pior que esperar.
 */
function useMarcaPublica(ativa = true): MarcaPublica | null | undefined {
  const { sugerirTema } = useTheme();
  const { slug: slugDoCaminho } = useParams();
  const [params] = useSearchParams();
  // `?porta=` é o slug que a porta `/entrar/:slug` passa adiante para
  // "Esqueci minha senha" e as outras telas (ver `usePorta`).
  const slugDaRota = slugDoCaminho ?? params.get('porta') ?? undefined;
  // `ativa = false` na porta da equipe: em `cima.yardsuite.com.br/yard` ela
  // não é da CIMA, e a marca dela não entra nem na aba do navegador.
  const slug = ativa ? (slugDaRota ?? clienteDoHost(window.location.host) ?? null) : null;
  const [marca, setMarca] = useState<MarcaPublica | null | undefined>(slug ? undefined : null);

  useEffect(() => {
    if (!slug || !supabase) return;
    let vivo = true;
    let tentativa = 0;

    // Falha de rede/RPC não é "cliente não existe": tratá-la como tal manda o
    // visitante para um 404 por causa de um problema passageiro. Enquanto
    // houver erro, o estado fica em `undefined` (tela de espera) e tenta de
    // novo, até três vezes; só depois disso desiste e mostra "não existe".
    const buscar = () => {
      supabase!
        .rpc('marca_publica', { p_slug: slug })
        .then(({ data, error }) => {
          if (!vivo) return;
          // A RPC responde `{ erro: 'muitas_tentativas' }` (0087) quando o
          // mesmo IP passa de 20 chamadas por minuto. Antes isso virava uma
          // "marca" sem nome: a porta abria com o título vazio e a cor da Yard.
          // É falha passageira, e tem o mesmo tratamento de uma falha de rede.
          const limite = !!data && typeof data === 'object' && 'erro' in data;
          if (error || limite) {
            console.error('marca_publica falhou', error ?? data);
            tentativa += 1;
            if (tentativa <= 3) setTimeout(buscar, 800 * tentativa);
            else setMarca(null);
            return;
          }
          setMarca((data as MarcaPublica) ?? null);
        });
    };
    buscar();

    return () => {
      vivo = false;
    };
  }, [slug]);

  // O tema que a marca pede, já na porta: o logo do cliente foi desenhado para
  // um fundo, e é esse fundo que ele precisa encontrar.
  useEffect(() => {
    sugerirTema(marca?.brand_theme);
  }, [marca?.brand_theme, sugerirTema]);

  // O ícone da marca também na aba, já na tela de entrada.
  //
  // V33/V34: sem devolver o favicon/título de antes, a aba ficava com o
  // ícone e o "Yard Suite | {cliente}" do ÚLTIMO cliente cuja porta a
  // pessoa visitou - inclusive depois de sair dali para `/login` (porta da
  // equipe, sem marca nenhuma) ou para a porta de outro cliente, sem passar
  // por reload de página (o caminho antigo `/entrar/:slug` é a mesma
  // origem, então é troca de rota do SPA, não navegação de verdade).
  useEffect(() => {
    const icone = marca?.icon_url || marca?.logo_url;
    if (!icone) return;
    let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
    const linkJaExistia = !!link;
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    const anteriorHref = link.href;
    const anteriorTitulo = document.title;
    link.href = icone;
    if (marca?.nome) document.title = `Yard Suite | ${marca.nome}`;
    return () => {
      if (linkJaExistia) {
        if (link) link.href = anteriorHref;
      } else {
        link?.remove();
      }
      document.title = anteriorTitulo;
    };
  }, [marca]);

  return marca;
}

/**
 * A porta por onde a pessoa entrou, para os links entre as telas de acesso
 * não perderem o cliente.
 *
 * No subdomínio (`cima.yardsuite.com.br`) o host já diz de quem é a porta, e
 * nada muda. No caminho `/entrar/cima` (localhost, e quem ainda não apontou o
 * DNS), "Esqueci minha senha" levava a `/esqueci-senha` cru: a recuperação
 * abria com a casca genérica da yard suite, e o "Voltar" dela caía em
 * `/login`, que fora do subdomínio é a porta da EQUIPE Yard. Agora o slug vai
 * junto em `?porta=` e a volta é para `/entrar/{slug}`.
 */
function usePorta() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const pedido = slug ?? params.get('porta');
  const pelaRota = pedido && !clienteDoHost(window.location.host) ? pedido : null;
  return {
    entrada: pelaRota ? `/entrar/${encodeURIComponent(pelaRota)}` : '/login',
    com: (caminho: string) =>
      pelaRota ? `${caminho}${caminho.includes('?') ? '&' : '?'}porta=${encodeURIComponent(pelaRota)}` : caminho,
  };
}

function useErro() {
  const [erro, setErro] = useState<string | null>(null);
  const traduzir = (m: string) => {
    if (/invalid login credentials/i.test(m)) return 'E-mail ou senha incorretos.';
    if (/email not confirmed/i.test(m)) return 'Confirme seu e-mail antes de entrar.';
    if (/user already registered/i.test(m)) return 'Já existe uma conta com este e-mail.';
    if (/password should be at least/i.test(m)) return 'A senha precisa de pelo menos 6 caracteres.';
    if (/slug_em_uso/i.test(m)) return 'Este endereço já está em uso por outra organização.';
    if (/cadastro_fechado/i.test(m))
      return 'Contas são criadas pela Yard. Peça o convite a quem contratou o produto.';
    if (/rate limit/i.test(m)) return 'Muitas tentativas. Aguarde um instante.';
    return m;
  };
  return { erro, setErro: (m: string | null) => setErro(m === null ? null : traduzir(m)) };
}

/**
 * Entrou pela porta de um cliente: a organização ativa passa a ser a dele.
 *
 * Quem atende dois clientes tem duas organizações na mesma conta, e a ativa é a
 * última que usou. Sem isto, entrar por `russirussi.yardsuite.com.br` podia
 * abrir o painel da CIMA: a porta certa levando ao lugar errado, que é pior do
 * que porta nenhuma, porque a pessoa confia no endereço que digitou.
 */
function PortaCerta({ marca, next }: { marca: MarcaPublica | null; next: string }) {
  const { org, orgs, switchOrg, adminPlataforma, entrarNaOrg } = useAuth();
  const irParaAConta = useIrParaAConta();
  const alvo = marca ? orgs.find((o) => o.slug === marca.slug) : undefined;
  const precisaTrocar = !!alvo && org?.id !== alvo.id;
  /*
   * A equipe Yard pela porta de um cliente de que não é membro: entra na
   * conta dele em visita, o mesmo "Entrar na conta" do console (registrado
   * por `yard_entrar_na_org`). Antes ela atravessava a porta e caía no painel
   * da própria Yard - em produção, em outro endereço - e precisava voltar ao
   * console para chegar à CIMA, que era aonde tinha ido.
   */
  const visitar = !!marca && !alvo && adminPlataforma && org?.slug !== marca.slug;
  const [trocando, setTrocando] = useState(precisaTrocar || visitar);

  useEffect(() => {
    if (!precisaTrocar || !alvo) return;
    let vivo = true;
    void switchOrg(alvo.id).finally(() => {
      if (vivo) setTrocando(false);
    });
    return () => {
      vivo = false;
    };
    // `switchOrg` fica de fora: ele muda de identidade a cada render do
    // contexto, e entraria em laço trocando de organização para sempre.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [precisaTrocar, alvo?.id]);

  useEffect(() => {
    if (!visitar || !marca || !supabase) return;
    let vivo = true;
    const sb = supabase;
    // Falhou (rede, permissão): segue o caminho de antes, o painel da Yard.
    void (async () => {
      try {
        const { data } = await sb.from('organizations').select('id').eq('slug', marca.slug).maybeSingle();
        if (data?.id) await entrarNaOrg(data.id as string);
      } catch {
        /* o caminho de antes */
      } finally {
        if (vivo) setTrocando(false);
      }
    })();
    return () => {
      vivo = false;
    };
    // Mesmo motivo do efeito de cima para `entrarNaOrg` ficar de fora.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitar, marca?.slug]);

  /**
   * Achado novo (mesmo padrão A1/A3, fora da citação original da auditoria):
   * entrar pela porta antiga `/entrar/:slug`, fora do subdomínio do cliente,
   * monta `next` como `/{slug}/home` (comentário em `Login`, abaixo) - e essa
   * raiz é justamente onde `app/Router.tsx` NÃO monta o bloco de rotas do
   * cliente (mesma decisão documentada em `useIrParaAConta`, `core/rotas.tsx`).
   * Um `<Navigate>` comum caía sempre em 404 nesse caminho. Só troca de HOST
   * quando ainda não se está no subdomínio certo - login pelo próprio
   * subdomínio (o caminho comum em produção) e localhost (sem vhost curinga)
   * seguem sendo um `<Navigate>` local, sem o custo de um reload à toa.
   */
  const host = typeof window !== 'undefined' ? window.location.host : '';
  const slugDoAlvo = marca?.slug ?? null;
  const precisaTrocarHost = !!slugDoAlvo && !ehLocalhost(host) && clienteDoHost(host) !== slugDoAlvo;

  useEffect(() => {
    if (trocando || !precisaTrocarHost || !slugDoAlvo) return;
    // `next` pode ter chegado prefixado com o próprio slug (montado assim
    // porque, no momento em que foi calculado, também não se estava no
    // subdomínio do cliente) - tira o prefixo antes de saltar de host, senão
    // `irParaAConta` monta `{slug}.yardsuite.com.br/{slug}/home`.
    const destino = next.startsWith(`/${slugDoAlvo}/`)
      ? next.slice(slugDoAlvo.length + 1)
      : next === `/${slugDoAlvo}`
        ? '/home'
        : next;
    void irParaAConta(slugDoAlvo, destino);
  }, [trocando, precisaTrocarHost, slugDoAlvo, next, irParaAConta]);

  if (trocando || precisaTrocarHost) return <PortaEsperando />;
  return <Navigate to={next} replace />;
}

/**
 * A porta enquanto a marca do cliente não chegou. Meio segundo de espera é
 * melhor que a tela piscar amarela e virar a marca dele depois.
 */
function PortaEsperando() {
  // Nenhuma marca enquanto espera, nem a da Yard: o canto do logo, o nome e o
  // formulário aparecem como esqueletos no mesmo lugar e no mesmo tamanho em
  // que a porta vai abrir, para nada pular quando a marca chegar.
  return (
    <div className="app-auth suite-modern auth-hub--esperando" aria-busy="true">
      <aside className="auth-marca">
        <div className="auth-lk"><div className="skeleton" style={{ width: 132, height: 36 }} /></div>
        <div className="auth-org">
          <div className="skeleton" style={{ width: '28%', height: 10 }} />
          <div className="skeleton auth-esqueleto-nome" style={{ width: '62%' }} />
          <div className="skeleton" style={{ width: '46%', height: 14, marginTop: 'var(--e5)' }} />
        </div>
        <div className="auth-rodape-marca"><div className="skeleton" style={{ width: 120, height: 10 }} /></div>
      </aside>
      <div className="auth-lado">
        <div className="auth-card auth-carregando">
          <span className="sr">Carregando a porta de entrada</span>
          <div className="skeleton" style={{ width: '40%', height: 30 }} />
          <div className="skeleton" style={{ width: '70%', height: 12 }} />
          <div className="skeleton" style={{ width: '18%', height: 12, marginTop: 'var(--e3)' }} />
          <div className="skeleton" style={{ width: '100%', height: 48 }} />
          <div className="skeleton" style={{ width: '18%', height: 12 }} />
          <div className="skeleton" style={{ width: '100%', height: 48 }} />
          <div className="skeleton" style={{ width: '100%', height: 50, borderRadius: 999, marginTop: 'var(--e2)' }} />
        </div>
      </div>
    </div>
  );
}

/* ================= Entrar ================= */

/**
 * Duas portas para o mesmo prédio.
 *
 * `/login` é a do cliente: entra e vai para o painel da organização dele.
 * `/yard` é a da equipe: entra e vai para o console de clientes. A porta não
 * autoriza nada por si: quem decide continua sendo `platform_admins` no banco.
 * Ela existe porque as duas pessoas chegam com expectativas diferentes, e
 * mandar o cliente para o console (ou você para um painel vazio) confunde os
 * dois lados.
 */
export function Login({
  modo = 'cliente',
  /**
   * Rota de caminho na raiz (`/cima`): o slug tem de ser de um cliente de
   * verdade. Sem isto, qualquer palavra solta na URL abriria uma tela de
   * entrada, e `/cimaa` pediria senha em vez de dizer que não existe.
   */
  exigeCliente = false,
}: {
  modo?: 'cliente' | 'yard';
  exigeCliente?: boolean;
}) {
  const { status, adminPlataforma, orgs, signOut } = useAuth();
  const irParaAConta = useIrParaAConta();
  const [params] = useSearchParams();
  const yard = modo === 'yard';
  const marca = useMarcaPublica(!yard);
  const porta = usePorta();
  const { slug: slugDaRota } = useParams();
  // Depois de entrar, a pessoa vai para a área dela, que agora carrega o slug:
  // `/russirussi/home`. O `next` explícito ganha, porque ele veio do guarda de
  // rota e já é o endereço completo de onde ela tentou chegar.
  //
  // O prefixo só entra quando o caminho ainda precisa dele: em
  // `{slug}.yardsuite.com.br` o subdomínio JÁ é o slug, e `/home` é a rota
  // certa - prefixar de novo mandava para `/russirussi/home`, que não existe
  // nesse host e caía num 404. `comSlug` já sabe disso (é a mesma regra que
  // `Link`/`useNavigate` usam em toda a área logada).
  const noSubdominio = !!clienteDoHost(window.location.host);
  const next =
    params.get('next') ??
    (yard ? '/admin/clientes' : marca ? (comSlug('/home', noSubdominio ? null : marca.slug) as string) : '/');
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [enviando, setEnviando] = useState(false);
  /* Validação inline (`ROTAS-AUTH.md`, seção 3): o erro aparece ao SAIR do
     campo, não a cada tecla - acusar "e-mail inválido" enquanto a pessoa
     ainda está digitando a primeira letra é ruído, não ajuda. */
  const [emailTocado, setEmailTocado] = useState(false);
  const { erro, setErro } = useErro();

  // B1: o handoff entre `yardsuite.com.br` e `{cliente}.yardsuite.com.br`
  // (`HandoffSubdominio.tsx`) manda para cá com `?erro=sessao` quando a
  // sessão não pegou do outro lado - sem isto a pessoa via a porta de
  // entrada normal, sem pista nenhuma de que tinha acabado de tentar entrar.
  useEffect(() => {
    if (params.get('erro') === 'sessao') {
      setErro('Não deu para confirmar sua entrada. Tente de novo.');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Entrou pela porta da equipe sem ser da equipe: não adianta seguir, o
  // console recusa. Melhor dizer aqui, com o caminho de volta.
  if (status === 'ready' && yard && !adminPlataforma) {
    return (
      <Card
        title="Esta porta é da equipe Yard"
        subtitle="Sua conta entrou, mas não é uma conta de plataforma."
      >
        <Banner tone="warning">
          Se você é cliente, use a entrada normal. Se deveria ter acesso ao
          console, peça a um administrador para incluir seu usuário.
        </Banner>
        <Link className="btn btn-primary" to="/home">
          Ir para o meu painel
        </Link>
        {/* Era um link para `/login`, que com a sessão de pé abria esta mesma
            tela de novo: o botão não fazia nada. Sair é o que ele promete. */}
        <Button variant="ghost" onClick={() => void signOut()}>
          Entrar com outro e-mail
        </Button>
      </Card>
    );
  }

  // ── A porta do cliente só abre para quem é do cliente ────────────────────
  //
  // Até aqui a porta estava só pintada: mostrava a marca certa e aceitava
  // qualquer conta da base. Quem era da CIMA entrava em `russirussi.yardsuite`
  // e caía no painel da CIMA, com a marca da Russi Russi na tela de trás. Não
  // vazava dado nenhum, porque a RLS continua sendo por organização, mas a
  // porta prometia uma coisa e entregava outra.
  //
  // A conferência é aqui e não antes da senha de propósito: antes dela não
  // existe sessão, e dizer "você não é daqui" para quem ainda não provou quem
  // é seria contar quem tem conta em qual cliente para qualquer visitante.
  //
  // A equipe Yard atravessa: é ela que entra na conta do cliente para dar
  // suporte, e é o banco que confere isso, não esta tela.
  const doorEstranha =
    status === 'ready' &&
    !yard &&
    !!marca &&
    !adminPlataforma &&
    !orgs.some((o) => o.slug === marca.slug);

  if (doorEstranha) {
    // A saída é a conta da própria pessoa. Antes era "Ir para a entrada
    // padrão", `/login` - que fora do endereço de um cliente é a porta da
    // equipe Yard: o cliente da Tricon barrado na porta da CIMA era mandado
    // para "Entrar na Yard".
    const minha = orgs[0];
    return (
      <Card marca={marca} title={`Esta entrada é da ${marca.nome}`}>
        <Banner tone="warning" title="Sua conta não pertence a esta organização.">
          Você entrou com uma conta que existe, mas que não é da {marca.nome}. Cada cliente tem a
          entrada dele, e uma não serve para a outra.
        </Banner>
        {minha && (
          <Button onClick={() => void irParaAConta(minha.slug, '/home')}>
            Ir para a {minha.name}
          </Button>
        )}
        <Button
          variant={minha ? 'ghost' : 'secondary'}
          onClick={() => {
            void signOut();
          }}
        >
          Sair e tentar outro e-mail
        </Button>
      </Card>
    );
  }

  if (status === 'ready') return <PortaCerta marca={yard ? null : (marca ?? null)} next={next} />;
  if (status === 'no-org') return <Navigate to="/criar-organizacao" replace />;
  // C1: quem já tem sessão e a organização está suspensa cai aqui vindo de
  // `/login` direto (esta tela não passa pelo `RouteGuard`, que é quem faz
  // este desvio no resto do painel). `!adminPlataforma` no mesmo espírito do
  // C3: a equipe Yard que também é membro da própria organização suspensa
  // tem o console, nunca fica presa em `/suspended`.
  if (status === 'suspended' && !adminPlataforma) return <Navigate to="/suspended" replace />;

  // `marca === undefined` ainda está carregando; `null` é resposta: não existe
  // cliente com esse endereço.
  if (exigeCliente && marca === null) return <NotFound />;

  // A porta do cliente espera a marca chegar antes de pintar.
  if (marca === undefined) return <PortaEsperando />;

  // `/entrar/:slug` com um endereço que não é de cliente nenhum. Antes abria
  // a porta genérica da yard suite e aceitava a senha, como se o endereço
  // estivesse certo; a pessoa só descobria o engano dentro do painel.
  if (!yard && slugDaRota && marca === null) {
    return (
      <Card title="Não achamos esta entrada" subtitle={`Nenhuma empresa usa o endereço "${slugDaRota}".`}>
        <Banner tone="warning" title="Confira o endereço.">
          Cada empresa entra pelo endereço dela, como o que veio no convite. Se você copiou o link,
          veja se ele veio inteiro.
        </Banner>
        <Link className="btn btn-secondary" to="/yard">
          Sou da equipe Yard
        </Link>
      </Card>
    );
  }

  // C2: `marca_publica` deixou de esconder a conta suspensa (RPC agora
  // devolve `status` sempre) - antes disso o cliente inadimplente nem
  // encontrava a própria porta, só um 404 genérico. Sem sessão ainda, então
  // isto vem ANTES do formulário: preencher e-mail/senha para uma conta que
  // vai recusar o login de qualquer forma só adiava a mesma notícia.
  if (!yard && marca?.status === 'suspended') {
    return (
      <Card marca={marca} title={`A conta da ${marca.nome} está suspensa`}>
        <Banner tone="warning">
          O acesso está temporariamente bloqueado. Fale com quem administra esta conta para
          regularizar o plano.
        </Banner>
      </Card>
    );
  }

  async function entrar(e: FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setErro(null);
    const { error } = await supabase!.auth.signInWithPassword({ email, password: senha });
    // No acerto o botão segue girando: quem leva adiante é a troca de
    // `status`, e o botão voltar a "Entrar" por meio segundo antes do painel
    // abrir parecia que nada tinha acontecido.
    if (error) {
      setErro(error.message);
      setEnviando(false);
    }
  }

  return (
    <Card
      marca={yard ? null : marca}
      equipe={yard}
      // O painel de marca já diz de quem é a porta; o título do cartão diz o
      // que se faz nela.
      title={yard ? 'Entrar na Yard' : 'Entrar'}
      subtitle={
        yard
          ? 'Console da equipe: clientes, produtos contratados e suporte.'
          : marca
            ? 'Entre com a conta que sua equipe usa aqui.'
            : 'Acesse sua conta da Yard Suite.'
      }
    >
      <form className="stack" onSubmit={entrar}>
        <Field
          label="E-mail"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => setEmailTocado(true)}
          autoComplete="email"
          required
          error={emailTocado && !email.trim() ? 'Informe o e-mail da sua conta.' : undefined}
        />
        <CampoDeSenha
          label="Senha"
          value={senha}
          onChange={setSenha}
          autoComplete="current-password"
          required
        />
        {erro && <Banner tone="danger">{erro}</Banner>}
        <Button type="submit" size="lg" loading={enviando}>
          Entrar
        </Button>
      </form>
      <div className="row-between body-sm">
        {/* 44px de alvo (CONTRATO.md, 8): o texto continua do mesmo tamanho,
            a área que responde ao dedo é que cresce. */}
        <Link to={porta.com('/esqueci-senha')} className="muted auth-link">
          Esqueci minha senha
        </Link>
        {yard ? (
          // Não há mais uma entrada genérica de cliente para apontar: cada um
          // entra pelo endereço dele, `/cima`, `/russirussi`. Mandar para
          // `/login` daria volta a esta mesma tela.
          <span className="muted">Cliente entra pelo endereço da empresa dele.</span>
        ) : marca?.site_url ? (
          // Quem chega aqui costuma vir do site do cliente. O caminho de volta
          // é parte da porta, não um extra.
          <a href={marca.site_url} target="_blank" rel="noopener noreferrer" className="muted">
            Ir para o site
          </a>
        ) : (
          <Link to="/yard" className="muted">
            Sou da equipe Yard
          </Link>
        )}
      </div>

      {marca && (
        <a
          className="assinatura-yard assinatura-auth"
          href="https://yardhub.com.br/"
          target="_blank"
          rel="noopener noreferrer"
        >
          <span className="marca-yard" aria-hidden="true">
            <Marca tamanho="inherit" />
          </span>
          <span className="assinatura-yard__texto">
            <strong>Uma solução Yard</strong>
            <span className="assinatura-yard__convite">
              Conheça nossos serviços
              <span className="assinatura-yard__seta" aria-hidden="true">
                <Icone.SetaDireita tamanho={13} />
              </span>
            </span>
          </span>
        </a>
      )}
    </Card>
  );
}

/* ================= Vista pura, para o catálogo ================= */

export type TelaDeAcesso =
  | 'entrar'
  | 'entrar-equipe'
  | 'recuperar'
  | 'convite'
  | 'confirmar-email'
  | 'nova-senha';

/**
 * A porta de entrada sem efeito nenhum: mesmo `Card`, mesmos campos e
 * banners das telas reais, mas sem Supabase, sem navegação e sem sessão.
 * É o que a galeria (`/_ds#acesso-*`) monta, para a prévia ser a tela de
 * verdade e não um desenho paralelo. `estado`:
 *   `cheio`       o formulário como abre;
 *   `erro`        a resposta de erro de cada tela (senha errada, link ou
 *                 convite expirado, e-mail já cadastrado);
 *   `vazio`       o pós-envio (link mandado, e-mail confirmado);
 *   `carregando`  a casca esperando a marca chegar.
 */
export function VistaDeAcesso({
  tela,
  estado = 'cheio',
  marca = null,
}: {
  tela: TelaDeAcesso;
  estado?: 'cheio' | 'erro' | 'vazio' | 'carregando';
  marca?: MarcaPublica | null;
}) {
  const [senha, setSenha] = useState('');
  const equipe = tela === 'entrar-equipe';

  if (estado === 'carregando') return <PortaEsperando />;

  if (tela === 'entrar' || tela === 'entrar-equipe') {
    return (
      <Card
        marca={equipe ? null : marca}
        equipe={equipe}
        title={equipe ? 'Entrar na Yard' : 'Entrar'}
        subtitle={
          equipe
            ? 'Console da equipe: clientes, produtos contratados e suporte.'
            : marca
              ? 'Entre com a conta que sua equipe usa aqui.'
              : 'Acesse sua conta da Yard Suite.'
        }
      >
        <form className="stack" onSubmit={(e) => e.preventDefault()}>
          <Field label="E-mail" type="email" defaultValue={estado === 'erro' ? 'ana@agencia.com.br' : ''} autoComplete="email" required />
          <CampoDeSenha label="Senha" value={estado === 'erro' ? 'senha-errada' : senha} onChange={setSenha} autoComplete="current-password" required />
          {estado === 'erro' && <Banner tone="danger">E-mail ou senha incorretos.</Banner>}
          <Button type="submit" size="lg">Entrar</Button>
        </form>
        <div className="row-between body-sm">
          <a href="#acesso-recuperar-senha" className="muted auth-link">Esqueci minha senha</a>
          {equipe ? (
            <span className="muted">Cliente entra pelo endereço da empresa dele.</span>
          ) : (
            <a href="#acesso-porta-da-equipe" className="muted">Sou da equipe Yard</a>
          )}
        </div>
        {marca && !equipe && (
          <a className="assinatura-yard assinatura-auth" href="https://yardhub.com.br/" target="_blank" rel="noopener noreferrer">
            <span className="marca-yard" aria-hidden="true"><Marca tamanho="inherit" /></span>
            <span className="assinatura-yard__texto">
              <strong>Uma solução Yard</strong>
              <span className="assinatura-yard__convite">
                Conheça nossos serviços
                <span className="assinatura-yard__seta" aria-hidden="true"><Icone.SetaDireita tamanho={13} /></span>
              </span>
            </span>
          </a>
        )}
      </Card>
    );
  }

  if (tela === 'recuperar') {
    return (
      <Card title="Recuperar senha" subtitle="Mandamos um link para você criar uma senha nova.">
        {estado === 'vazio' ? (
          <>
            <Banner tone="info">Se existir uma conta com esse e-mail, o link de recuperação chega em instantes.</Banner>
            <a className="btn btn-secondary" href="#acesso-entrar" style={{ width: '100%' }}>Voltar para o login</a>
          </>
        ) : (
          <form className="stack" onSubmit={(e) => e.preventDefault()}>
            <Field label="E-mail" type="email" defaultValue={estado === 'erro' ? 'ana@agencia' : ''} required error={estado === 'erro' ? 'Informe um e-mail completo, como nome@empresa.com.br.' : undefined} />
            <Button type="submit" size="lg">Enviar link</Button>
            <a href="#acesso-entrar" className="body-sm muted">Voltar</a>
          </form>
        )}
      </Card>
    );
  }

  if (tela === 'convite') {
    if (estado === 'erro') {
      return (
        <Card title="Este convite não vale mais" subtitle="O link expirou ou já foi usado uma vez.">
          <Banner tone="warning" title="Peça um convite novo.">
            Quem administra a conta manda outro em Configurações, Usuários. O e-mail chega com um link novo.
          </Banner>
          <a className="btn btn-ghost" href="#acesso-entrar">Voltar para o login</a>
        </Card>
      );
    }
    return (
      <Card title="Aceitar convite" subtitle="Crie sua conta com o e-mail que recebeu o convite.">
        <Banner tone="info" title="Como funciona.">
          Ao criar a conta, você entra automaticamente na organização que te convidou, com o papel definido por quem convidou.
        </Banner>
        <Field label="E-mail do convite" type="email" defaultValue="ana@agencia.com.br" readOnly />
        <Button size="lg" style={{ width: '100%' }}>Criar minha conta</Button>
        <p className="body-sm muted">Já tem conta? <a href="#acesso-entrar">Entrar</a></p>
      </Card>
    );
  }

  if (tela === 'confirmar-email') {
    return (
      <Card title="Confirme seu e-mail">
        <Banner tone="info" title="Enviamos um link para você.">
          Abra o e-mail e toque no link para ativar a conta. Se não chegar em alguns minutos, confira a caixa de spam.
        </Banner>
        <a className="btn btn-secondary" href="#acesso-entrar" style={{ width: '100%' }}>Voltar para o login</a>
      </Card>
    );
  }

  // nova-senha
  if (estado === 'erro') {
    return (
      <Card title="Este link não vale mais" subtitle="Links de redefinição expiram, e cada um serve uma vez só.">
        <Banner tone="warning" title="Peça um link novo.">
          Volte para a recuperação de senha e peça outro. Ele chega no mesmo e-mail.
        </Banner>
        <a className="btn btn-primary" href="#acesso-recuperar-senha">Pedir um link novo</a>
      </Card>
    );
  }
  return (
    <Card title="Nova senha" subtitle="Escolha uma senha para voltar a acessar.">
      <form className="stack" onSubmit={(e) => e.preventDefault()}>
        <CampoDeSenha label="Nova senha" value={senha} onChange={setSenha} autoComplete="new-password" required />
        <ChecklistDeSenha senha={senha} />
        <Button type="submit" size="lg" disabled={!senhaValida(senha)}>Salvar senha</Button>
      </form>
    </Card>
  );
}

/* ================= Criar conta ================= */

export function Signup() {
  const marca = useMarcaPublica();
  const porta = usePorta();
  const [params] = useSearchParams();
  const [nome, setNome] = useState('');
  // Quem vem de "Aceitar convite" chega com o e-mail do convite na URL; antes
  // ele se perdia aqui e a pessoa digitava de novo (e podia errar).
  const [email, setEmail] = useState(() => params.get('email') ?? '');
  const [senha, setSenha] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [pronto, setPronto] = useState(false);
  const [jaExiste, setJaExiste] = useState(false);
  const { erro, setErro } = useErro();

  async function criar(e: FormEvent) {
    e.preventDefault();
    if (!senhaValida(senha)) return;
    setEnviando(true);
    setErro(null);
    setJaExiste(false);
    const { data, error } = await supabase!.auth.signUp({
      email,
      password: senha,
      options: { data: { name: nome } },
    });
    setEnviando(false);
    if (error) {
      /* E-MAIL JÁ CADASTRADO: AQUI A RESPOSTA É DIRETA (seção 3).
         É o oposto deliberado de `/esqueci-senha`, que responde a mesma frase
         exista ou não a conta. A diferença não é descuido: em "esqueci a
         senha" alguém poderia usar a tela para descobrir quem tem conta na
         Yard, e por isso ela é muda. Aqui a pessoa está criando algo novo -
         esconder que o e-mail já existe só a faria tentar de novo achando que
         errou a digitação, quando o caminho dela é entrar ou recuperar. */
      const m = error.message.toLowerCase();
      if (m.includes('already registered') || m.includes('already been registered') || m.includes('user already')) {
        setJaExiste(true);
        return;
      }
      return setErro(error.message);
    }
    // Sem sessão significa que o projeto exige confirmação por e-mail.
    if (!data.session) setPronto(true);
  }

  if (marca === undefined) return <PortaEsperando />;

  if (pronto) {
    return (
      <Card marca={marca} title="Confirme seu e-mail">
        <Banner tone="info" title="Enviamos um link para você.">
          Abra o e-mail que mandamos para <strong>{email}</strong> e confirme a conta para entrar.
        </Banner>
        <Link to={porta.entrada}>
          <Button variant="secondary" style={{ width: '100%' }}>
            Voltar para o login
          </Button>
        </Link>
      </Card>
    );
  }

  return (
    <Card marca={marca} title="Criar conta" subtitle="Comece criando seu acesso; a organização vem no passo seguinte.">
      <form className="stack" onSubmit={criar}>
        <Field label="Seu nome" value={nome} onChange={(e) => setNome(e.target.value)} autoComplete="name" required />
        <Field
          label="E-mail"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          autoComplete="email"
          required
        />
        <div>
          <CampoDeSenha
            label="Senha"
            value={senha}
            onChange={setSenha}
            autoComplete="new-password"
            required
          />
          <ChecklistDeSenha senha={senha} />
        </div>
        {jaExiste && (
          <Banner tone="warning" title="Este e-mail já tem uma conta.">
            Você pode <Link to={porta.entrada}>entrar</Link> ou{' '}
            <Link to={porta.com('/esqueci-senha')}>criar uma senha nova</Link>.
          </Banner>
        )}
        {erro && <Banner tone="danger">{erro}</Banner>}
        <Button type="submit" size="lg" loading={enviando} disabled={!senhaValida(senha)}>
          Criar conta
        </Button>
      </form>
      <p className="body-sm muted">
        Já tem conta? <Link to={porta.entrada}>Entrar</Link>
      </p>
    </Card>
  );
}

/* ================= Esqueci a senha ================= */

export function ForgotPassword() {
  // No endereço da CIMA, recuperar a senha continua sendo a porta da CIMA:
  // antes esta tela (e as de baixo) caía na casca genérica da yard suite,
  // amarela, como se a pessoa tivesse saído do cliente.
  const marca = useMarcaPublica();
  const porta = usePorta();
  const [email, setEmail] = useState('');
  const [enviado, setEnviado] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const { erro, setErro } = useErro();

  async function enviar(e: FormEvent) {
    e.preventDefault();
    setEnviando(true);
    setErro(null);
    const { error } = await supabase!.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/redefinir-senha`,
    });
    setEnviando(false);
    if (error) return setErro(error.message);
    setEnviado(true);
  }

  if (marca === undefined) return <PortaEsperando />;

  return (
    <Card marca={marca} title="Recuperar senha" subtitle="Mandamos um link para você criar uma senha nova.">
      {enviado ? (
        <>
          {/* Resposta uniforme: não revelamos se o e-mail existe (documento 10.5). */}
          <Banner tone="info">
            Se existir uma conta com esse e-mail, o link de recuperação chega em instantes.
          </Banner>
          <Link to={porta.entrada}>
            <Button variant="secondary" style={{ width: '100%' }}>
              Voltar para o login
            </Button>
          </Link>
        </>
      ) : (
        <form className="stack" onSubmit={enviar}>
          <Field
            label="E-mail"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="email"
            required
          />
          {erro && <Banner tone="danger">{erro}</Banner>}
          <Button type="submit" size="lg" loading={enviando}>
            Enviar link
          </Button>
          <Link to={porta.entrada} className="body-sm muted">
            Voltar
          </Link>
        </form>
      )}
    </Card>
  );
}

/* ================= Redefinir senha ================= */

export function ResetPassword() {
  const marca = useMarcaPublica();
  const porta = usePorta();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [senha, setSenha] = useState('');
  const [confirma, setConfirma] = useState('');
  const [confirmaTocada, setConfirmaTocada] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const { erro, setErro } = useErro();

  /* Link expirado (seção 3): o Supabase devolve o motivo na querystring
     quando o token de recuperação já morreu. Mostrar o formulário nesse caso
     seria pedir uma senha que não teria como ser salva. */
  const expirado = params.get('error') !== null || params.get('error_code') !== null;
  /* `convite-enviar` manda o link do convite do Auth para cá com
     `?convite=1`: a pessoa entra pela primeira vez e precisa CRIAR a senha,
     não "voltar a acessar". Mesmo formulário, palavras certas. */
  const convite = params.get('convite') === '1';

  const confere = senha === confirma;
  const podeSalvar = senhaValida(senha) && confere;

  async function salvar(e: FormEvent) {
    e.preventDefault();
    if (!podeSalvar) return;
    setEnviando(true);
    setErro(null);
    const { error } = await supabase!.auth.updateUser({ password: senha });
    setEnviando(false);
    if (error) return setErro(error.message);
    navigate('/home', { replace: true });
  }

  if (marca === undefined) return <PortaEsperando />;

  if (expirado && convite) {
    return (
      <Card marca={marca} title="Este convite não vale mais" subtitle="O link expirou ou já foi usado uma vez.">
        <Banner tone="warning" title="Peça um convite novo.">
          Fale com quem te convidou e peça para convidar este e-mail de novo - o
          link novo chega no mesmo endereço.
        </Banner>
        <Link className="btn btn-ghost" to={porta.entrada}>
          Voltar para a entrada
        </Link>
      </Card>
    );
  }

  if (expirado) {
    return (
      <Card marca={marca} title="Este link não vale mais" subtitle="Links de redefinição expiram, e cada um serve uma vez só.">
        <Banner tone="warning" title="Peça um link novo.">
          É rápido: informe o e-mail de novo e mandamos outro na hora.
        </Banner>
        <Link className="btn btn-primary" to={porta.com('/esqueci-senha')}>
          Pedir link novo
        </Link>
      </Card>
    );
  }

  return (
    <Card marca={marca}
      title={convite ? 'Crie sua senha' : 'Nova senha'}
      subtitle={convite ? 'Último passo: com ela você entra na organização que te convidou.' : 'Escolha uma senha para voltar a acessar.'}
    >
      <form className="stack" onSubmit={salvar}>
        <div>
          <CampoDeSenha
            label="Nova senha"
            value={senha}
            onChange={setSenha}
            autoComplete="new-password"
            required
          />
          <ChecklistDeSenha senha={senha} />
        </div>
        <CampoDeSenha
          label="Confirme a nova senha"
          value={confirma}
          onChange={setConfirma}
          onBlur={() => setConfirmaTocada(true)}
          autoComplete="new-password"
          required
          error={confirmaTocada && confirma && !confere ? 'As duas senhas precisam ser iguais.' : undefined}
        />
        {erro && <Banner tone="danger">{erro}</Banner>}
        <Button type="submit" size="lg" loading={enviando} disabled={!podeSalvar}>
          Salvar senha
        </Button>
      </form>
    </Card>
  );
}

/* ================= Sem organização ================= */

/**
 * Desde que o cadastro fechou, ninguém monta a própria organização: quem vende
 * a conta é a Yard. Esta tela deixou de ser um formulário e virou o que
 * realmente acontece quando alguém chega aqui: a conta existe, o convite ainda
 * não. Oferecer o formulário era pior que não ter tela nenhuma, porque o botão
 * só sabia falhar.
 *
 * A rota continua sendo `/criar-organizacao` para não quebrar link antigo.
 */
export function CreateOrg() {
  const marca = useMarcaPublica();
  const porta = usePorta();
  const { status, org, orgs, switchOrg, signOut, refresh } = useAuth();
  const navigate = useNavigate();
  const irParaAConta = useIrParaAConta();
  const [entrando, setEntrando] = useState<string | null>(null);
  const [conferindo, setConferindo] = useState(false);
  const { erro, setErro } = useErro();

  /**
   * A2: ficar em `<Navigate to={comSlug('/home', slug)} />` só troca de
   * CAMINHO, e o painel do cliente mora no HOST dele em produção - a mesma
   * armadilha de `RaizDoPainel` (`app/Router.tsx`). `status` vira `'ready'`
   * assim que o `hydrate` da troca termina (efeito de `core/auth.tsx`, não
   * mais o clique em si - ver B6); é esse efeito, e não o clique, quem sabe
   * que já é hora de ir.
   */
  useEffect(() => {
    if (status === 'ready' && org?.slug) void irParaAConta(org.slug, '/home');
  }, [status, org?.slug, irParaAConta]);

  if (status === 'unauthed') return <Navigate to={porta.entrada} replace />;
  if (status === 'ready') return null;
  if (marca === undefined) return <PortaEsperando />;

  return (
    <Card marca={marca}
      title="Sua conta ainda não tem organização"
      subtitle="A organização é o espaço onde ficam os dados, a equipe e os apps. Ela é criada pela Yard quando o produto é contratado."
    >
      {/* O que acontece de verdade (`convite-enviar`, 0113): quem chega a
          esta tela já tem conta, então o convite não manda link para criar
          senha - ele libera o acesso na hora e, quando o e-mail transacional
          está configurado, manda um aviso. Antes a frase prometia "o convite
          chega na sua caixa de entrada e abre a conta na hora", e o convite
          da tela de Usuários não mandava e-mail nenhum nem ligava quem já
          tinha conta. O botão "Já fui convidado" existe porque esta tela não
          fica sabendo sozinha que o acesso foi liberado. */}
      <Banner tone="info">
        Se você já contratou, peça a quem administra a organização para
        convidar este e-mail. Como você já tem conta, o acesso é liberado assim
        que o convite for feito - você pode receber um aviso por e-mail. Depois
        disso, toque em "Já fui convidado".
      </Banner>

      {erro && <Banner tone="danger">{erro}</Banner>}

      {orgs.length > 0 && (
        <div className="stack" style={{ gap: 'var(--space-2)' }}>
          <span className="overline">Ou entre em uma que já existe</span>
          {orgs.map((o) => (
            <Button
              key={o.id}
              variant="secondary"
              loading={entrando === o.id}
              onClick={async () => {
                setEntrando(o.id);
                setErro(null);
                try {
                  // Não navega aqui: o efeito acima observa `status` e leva
                  // para a conta certa assim que o `hydrate` da troca
                  // terminar (B6 - `switchOrg` não espera mais o `hydrate`
                  // completar antes de devolver).
                  await switchOrg(o.id);
                } catch (e) {
                  setErro(e instanceof Error ? e.message : 'Não conseguimos abrir esta organização.');
                } finally {
                  setEntrando(null);
                }
              }}
            >
              {o.name}
            </Button>
          ))}
        </div>
      )}

      <Button
        variant="secondary"
        loading={conferindo}
        onClick={async () => {
          setConferindo(true);
          setErro(null);
          try {
            // `refresh` refaz o `hydrate`; se o vínculo já existe, `status`
            // vira 'ready' e o efeito lá em cima leva para a conta.
            await refresh();
          } catch (e) {
            setErro(e instanceof Error ? e.message : 'Não conseguimos conferir agora.');
          } finally {
            setConferindo(false);
          }
        }}
      >
        Já fui convidado
      </Button>

      <Button
        variant="ghost"
        onClick={async () => {
          await signOut();
          navigate(porta.entrada, { replace: true });
        }}
      >
        Sair e entrar com outro e-mail
      </Button>
    </Card>
  );
}

/* ================= Aceitar convite ================= */

export function AcceptInvite() {
  const marca = useMarcaPublica();
  const porta = usePorta();
  const { status, org } = useAuth();
  const irParaAConta = useIrParaAConta();
  const [params] = useSearchParams();
  const email = params.get('email') ?? '';
  /* Link expirado (`ROTAS-AUTH.md`, seção 3): quando o convite traz `error` na
     querystring (o Supabase devolve assim), a tela NÃO mostra o formulário -
     mostra por que o link morreu e como pedir outro. Formulário que não pode
     dar certo é pior que aviso nenhum. */
  const expirado = params.get('error') !== null || params.get('error_code') !== null;

  // Achado novo (mesmo padrão do A2/A1, fora da citação original da
  // auditoria): `<Navigate to={comSlug('/home', slug)} />` só troca de
  // CAMINHO - em produção o painel do cliente mora no HOST dele. Quem
  // aceitava um convite caía num 404 exatamente no momento em que a conta
  // acabava de nascer. Mesmo efeito de `CreateOrg`, acima: `status` vira
  // `'ready'` só depois que o `hydrate` da nova sessão terminar.
  useEffect(() => {
    if (status === 'ready' && org?.slug) void irParaAConta(org.slug, '/home');
  }, [status, org?.slug, irParaAConta]);

  if (status === 'ready') return null;
  if (marca === undefined) return <PortaEsperando />;

  if (expirado) {
    return (
      <Card marca={marca} title="Este convite não vale mais" subtitle="O link expirou ou já foi usado uma vez.">
        <Banner tone="warning" title="Peça um convite novo.">
          Convites têm prazo. Fale com quem te convidou e peça para reenviar - o
          link novo chega no mesmo e-mail.
        </Banner>
        <Link className="btn btn-ghost" to={porta.entrada}>
          Voltar para a entrada
        </Link>
      </Card>
    );
  }

  return (
    <Card marca={marca} title="Aceitar convite" subtitle="Crie sua conta com o e-mail que recebeu o convite.">
      <Banner tone="info" title="Como funciona.">
        Ao criar a conta, você entra automaticamente na organização que te
        convidou, com o papel definido por quem convidou.
      </Banner>
      {/* O e-mail aparece como CAMPO TRAVADO, não como texto no meio da frase
          (`ROTAS-AUTH.md`, seções 3 e 5: "usa `disabled` - a pessoa não deveria
          conseguir focar nem editar um valor que não é dela para mudar").
          Mostrar no formato de campo é o que deixa claro que este endereço é
          parte do que vai ser criado, e não um detalhe do aviso. */}
      {email && (
        <Field
          label="E-mail do convite"
          type="email"
          value={email}
          disabled
          readOnly
          onChange={() => {}}
          help="O convite vale só para este endereço."
        />
      )}
      <Link to={porta.com(`/criar-conta${email ? `?email=${encodeURIComponent(email)}` : ''}`)}>
        <Button size="lg" style={{ width: '100%' }}>
          Criar minha conta
        </Button>
      </Link>
      <p className="body-sm muted">
        Já tem conta? <Link to={porta.entrada}>Entrar</Link>
      </p>
    </Card>
  );
}
